import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import AdminLayout from "../components/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = ["perfume", "watch", "sunglasses", "bag", "candle", "incense", "accessory"];

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ slug: "", label: "", description: "", product_type: "perfume", collection: "", icon: "", sort_order: 100, active: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/categories`, { withCredentials: true });
      setItems(data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (cat) => {
    try {
      await axios.put(`${API}/admin/categories/${cat.slug}`, {
        label: cat.label, description: cat.description, product_type: cat.product_type,
        collection: cat.collection || null, icon: cat.icon, sort_order: cat.sort_order, active: cat.active,
      }, { withCredentials: true });
      toast.success("Catégorie mise à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Supprimer la catégorie ${slug} ?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${slug}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.slug !== slug));
      toast.success("Supprimé");
    } catch { toast.error("Impossible"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, collection: form.collection || null };
      const { data } = await axios.post(`${API}/admin/categories`, payload, { withCredentials: true });
      setItems((prev) => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
      setForm({ slug: "", label: "", description: "", product_type: "perfume", collection: "", icon: "", sort_order: 100, active: true });
      setShowAdd(false);
      toast.success("Catégorie créée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setField = (i, k, v) => setItems((prev) => prev.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  return (
    <AdminLayout current="categories">
      <div className="max-w-5xl" data-testid="admin-categories-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">Structure</p>
            <h1 className="font-serif text-4xl">Catégories</h1>
            <p className="text-white/50 text-sm mt-2">Créez et organisez vos univers produit. Les catégories inactives sont masquées du site.</p>
          </div>
          <button data-testid="add-category-btn"
            onClick={() => setShowAdd((v) => !v)}
            className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black hover:bg-white px-5 py-3 label-caps text-[11px] tracking-[0.25em] transition-colors">
            <Plus size={14} /> Nouvelle catégorie
          </button>
        </div>

        {showAdd && (
          <form onSubmit={create} className="border border-[color:var(--gold)]/30 p-6 mb-8 space-y-4" data-testid="new-category-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Slug (ex: bougies)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input-line" data-testid="cat-slug" />
              <input required placeholder="Label (ex: Bougies parfumées)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-line" data-testid="cat-label" />
              <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className="input-line" data-testid="cat-type">
                {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Sous-collection (Signature/Orient/vide)" value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} className="input-line" />
              <input placeholder="Icône Lucide (ex: Sparkles)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input-line" />
              <input type="number" placeholder="Ordre" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className="input-line" />
            </div>
            <textarea placeholder="Description courte" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 outline-none text-sm resize-none" />
            <button type="submit" data-testid="submit-new-category" className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black hover:bg-white px-5 py-3 label-caps text-[11px] tracking-[0.25em]"><Save size={14} /> Créer</button>
          </form>
        )}

        {loading && <p className="text-white/40">Chargement…</p>}
        <div className="space-y-3">
          {items.map((c, i) => (
            <div key={c.slug} className={`border p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center transition-colors ${c.active ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`cat-row-${c.slug}`}>
              <input value={c.label} onChange={(e) => setField(i, "label", e.target.value)} className="input-line md:col-span-3" placeholder="Label" />
              <code className="text-white/40 text-xs md:col-span-2">{c.slug}</code>
              <select value={c.product_type} onChange={(e) => setField(i, "product_type", e.target.value)} className="input-line md:col-span-2">
                {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" value={c.sort_order} onChange={(e) => setField(i, "sort_order", Number(e.target.value))} className="input-line md:col-span-1" />
              <label className="label-caps text-[10px] text-white/60 flex items-center gap-2 md:col-span-2">
                <input type="checkbox" checked={c.active} onChange={(e) => setField(i, "active", e.target.checked)} /> Actif
              </label>
              <div className="flex gap-2 md:col-span-2 justify-end">
                <button onClick={() => save(c)} className="inline-flex items-center gap-1.5 bg-[color:var(--gold)] text-black hover:bg-white px-3 py-1.5 label-caps text-[10px]" data-testid={`save-cat-${c.slug}`}><Save size={12} /></button>
                <button onClick={() => del(c.slug)} className="border border-white/15 hover:border-red-400 text-white/40 hover:text-red-400 px-3 py-1.5" data-testid={`del-cat-${c.slug}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
